// front/plugins/vue-analytics.js
// GA4 loader + shim with domain-based measurement ID selection.
// Injects $ga so Vue components and Vuex stores can call: this.$ga.event(...) / this.$ga.page(...)

import Cookie from 'js-cookie'

function pickMeasurementId(cfg) {
  const host = (typeof window !== 'undefined' && window.location.hostname) || ''
  const cleanHost = host.toLowerCase()

  const ids = (cfg && cfg.GA_IDS) || {}

  // UCC: ucc.myuse.ie and tool.myuse.ie
  if (/^(ucc|tool)\.myuse\.ie$/i.test(cleanHost)) {
    return ids.ucc || 'G-C4DZGQQFE2'
  }

  // UL: ul.myuse.ie
  if (/^ul\.myuse\.ie$/i.test(cleanHost)) {
    return ids.ul || 'G-3BQR68HR44'
  }

  // UCD: ucd.myuse.ie
  if (/^ucd\.myuse\.ie$/i.test(cleanHost)) {
    return ids.ucd || 'G-XKK8QKF0F8'
  }

  // TCD: tcd.myuse.ie
  if (/^tcd\.myuse\.ie$/i.test(cleanHost)) {
    return ids.tcd || 'G-WT5964TY6L'
  }

  // ATU: atu.myuse.ie
  if (/^atu\.myuse\.ie$/i.test(cleanHost)) {
    return ids.atu || 'G-7BFXRXY8GH'
  }

  // SETU placeholder, only enable once you have a GA ID for it
  if (/^setu\.myuse\.ie$/i.test(cleanHost)) {
    return ids.setu || 'G-FP9M8DTL93'
  }

  // Local/dev fallback
  return ids.ucc || 'G-C4DZGQQFE2'
}

function pickTenantKey() {
  const host = (typeof window !== 'undefined' && window.location.hostname) || ''
  const cleanHost = host.toLowerCase()

  if (/^(ucc|tool)\.myuse\.ie$/i.test(cleanHost)) return 'ucc'
  if (/^ul\.myuse\.ie$/i.test(cleanHost)) return 'ul'
  if (/^ucd\.myuse\.ie$/i.test(cleanHost)) return 'ucd'
  if (/^tcd\.myuse\.ie$/i.test(cleanHost)) return 'tcd'
  if (/^atu\.myuse\.ie$/i.test(cleanHost)) return 'atu'
  if (/^setu\.myuse\.ie$/i.test(cleanHost)) return 'setu'

  return 'ucc'
}

function isProduction(cfg) {
  const env =
    (cfg && (cfg.APP_ENV || cfg.appEnv)) ||
    (typeof process !== 'undefined' && process.env && process.env.APP_ENV) ||
    (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) ||
    'production'

  return String(env).toLowerCase() === 'production'
}

function ensureGtagBootstrap() {
  if (typeof window === 'undefined') return

  window.dataLayer = window.dataLayer || []

  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer.push(arguments)
    }
  }
}

function loadGtagScriptOnce(id) {
  if (typeof window === 'undefined' || !id) return

  const src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  const existed = document.querySelector(`script[src="${src}"]`)

  if (existed) return

  const s = document.createElement('script')
  s.async = true
  s.src = src
  document.head.appendChild(s)
}

function setConsent(granted) {
  if (!window.gtag) return

  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  })
}

function readAnalyticsOptIn() {
  const v = Cookie.get('allow-analytics')
  return v === '1' || v === 1 || v === true || v === 'true'
}

export default (context, inject) => {
  const noop = {
    enable() {},
    disable() {},
    set() {},
    event() {},
    page() {},
    _id: null,
    _tenant: null,
  }

  if (process.server) {
    inject('ga', noop)
    return
  }

  const cfg = context.$config || context.app?.$config || {}
  const tenantKey = pickTenantKey()
  const GA_ID = pickMeasurementId(cfg)

  if (!GA_ID || !/^G-[A-Z0-9]+$/i.test(GA_ID)) {
    console.warn('Google Analytics disabled: invalid GA ID', {
      tenant: tenantKey,
      id: GA_ID,
      host: window.location.hostname,
    })

    inject('ga', noop)
    return
  }

  ensureGtagBootstrap()

  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
  })

  loadGtagScriptOnce(GA_ID)

  const prod = isProduction(cfg)
  const optedIn = readAnalyticsOptIn()
  const initiallyEnabled = prod && optedIn

  window[`ga-disable-${GA_ID}`] = !initiallyEnabled

  window.gtag('js', new Date())

  window.gtag('config', GA_ID, {
    send_page_view: false,
    anonymize_ip: true,
  })

  setConsent(initiallyEnabled)

  const debugMode =
    typeof window !== 'undefined' &&
    /[?&]ga_debug=1(?:&|$)/.test(String(window.location.search || ''))

  function isEnabled() {
    return !window[`ga-disable-${GA_ID}`]
  }

  function sendPageView(path) {
    if (!window.gtag || !isEnabled()) return

    const p = path || window.location.pathname

    window.gtag('event', 'page_view', {
      page_location: window.location.origin + p,
      page_path: p,
      page_title: document.title,
      tenant: tenantKey,
      ...(debugMode ? { debug_mode: true } : {}),
    })
  }

  function resyncFromCookies() {
    const nowEnabled = isProduction(cfg) && readAnalyticsOptIn()

    window[`ga-disable-${GA_ID}`] = !nowEnabled

    setConsent(nowEnabled)

    window.gtag('config', GA_ID, {
      send_page_view: false,
      anonymize_ip: true,
      ...(debugMode ? { debug_mode: true } : {}),
    })

    if (nowEnabled) {
      const p = context.app.router?.currentRoute?.fullPath || window.location.pathname
      sendPageView(p)
    }
  }

  const GA = {
    _id: GA_ID,
    _tenant: tenantKey,

    enable() {
      resyncFromCookies()
    },

    disable() {
      window[`ga-disable-${GA_ID}`] = true
      setConsent(false)
    },

    set(key, value) {
      if (!window.gtag) return

      if (key === 'user_id') {
        window.gtag('config', GA_ID, {
          user_id: value,
        })

        window.gtag('set', 'user_properties', {
          user_id: value,
          tenant: tenantKey,
        })

        return
      }

      if (/^dimension\d+$/i.test(key)) {
        const props = {}
        props[key] = value
        props.tenant = tenantKey

        window.gtag('set', 'user_properties', props)
        return
      }

      const obj = {}
      obj[key] = value

      window.gtag('set', obj)
    },

    event(category, action, label, value) {
      if (!window.gtag || !isEnabled()) return

      const name = action || category || 'event'
      const params = {
        tenant: tenantKey,
      }

      if (category) params.event_category = String(category)
      if (label) params.event_label = String(label)
      if (value != null && !Number.isNaN(Number(value))) params.value = Number(value)
      if (debugMode) params.debug_mode = true

      window.gtag('event', name, params)
    },

    page(path) {
      sendPageView(path)
    },
  }

  inject('ga', GA)

  window.__myuse_set_analytics_consent = function () {
    resyncFromCookies()
  }

  const ignore = ['/administration']
  const router = context.app.router

  if (router) {
    router.afterEach((to) => {
      setTimeout(() => {
        if (ignore.includes(to.path)) return
        sendPageView(to.fullPath)
      }, 0)
    })
  } else {
    setTimeout(() => {
      sendPageView(window.location.pathname)
    }, 0)
  }

  let tries = 0
  const maxTries = 20

  const timer = setInterval(() => {
    tries++
    resyncFromCookies()

    if (tries >= maxTries) {
      clearInterval(timer)
    }
  }, 250)

  console.info('Google Analytics initialized', {
    tenant: tenantKey,
    id: GA_ID,
    host: window.location.hostname,
    enabled: initiallyEnabled,
  })
}